<script setup>
import { computed, watch, onBeforeUnmount } from 'vue'
import redHeartPng from '@/assets/images/emoji/red-heart.png'
import thumbsUpPng from '@/assets/images/emoji/thumbs-up.png'
import firePng from '@/assets/images/emoji/fire.png'
import faceWithTearsOfJoyPng from '@/assets/images/emoji/face-with-tears-of-joy.png'
import thumbsDownPng from '@/assets/images/emoji/thumbs-down.png'
import loudlyCryingFace from '@/assets/images/emoji/loudly-crying-face.png'
import { useChatStore } from '@/stores/chat.js'
import { storeToRefs } from 'pinia'

const props = defineProps({
  message: {
    type: Object,
    required: true
  }
})

const emojiIcons = new Map([
  ['heart', redHeartPng],
  ['thumbs_up', thumbsUpPng],
  ['fire', firePng],
  ['joy', faceWithTearsOfJoyPng],
  ['thumbs_down', thumbsDownPng],
  ['sob', loudlyCryingFace]
])

const reactionId = computed(() => props.message.reactionId ?? null)

const reactionIcon = computed(() => {
  return reactionId.value ? emojiIcons.get(reactionId.value) : null
})

const chatStore = useChatStore()

const {
  chatDialog
} = storeToRefs(chatStore)

// Трекер видимости сообщения для отметки реакции как прочитанной
let stopTracking = null

function startTrackingMessageVisibility (messageId) {
  const container = document.getElementById('messages')

  if (!container) return

  if (stopTracking) stopTracking()
  stopTracking = null

  let io = null
  let mo = null
  let finished = false

  const cleanup = () => {
    if (finished) return
    finished = true
    if (io) io.disconnect()
    if (mo) mo.disconnect()
    io = null
    mo = null
    stopTracking = null
  }

  const waitUntilVisible = (el) => {
    const elRect = el.getBoundingClientRect()
    const cRect = container.getBoundingClientRect()

    const alreadyVisible =
      elRect.bottom > cRect.top &&
      elRect.top < cRect.bottom &&
      elRect.right > cRect.left &&
      elRect.left < cRect.right

    if (alreadyVisible) {
      console.log('Message is visible:', messageId)

      setTimeout(() => {
        chatDialog.value.unreadReactionMessage = null
      }, 6000)

      cleanup()
      return
    }

    io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry && entry.isIntersecting) {
          console.log('Message is visible:', messageId)

          setTimeout(() => {
            chatDialog.value.unreadReactionMessage = null
          }, 6000)

          cleanup()
        }
      },
      {
        root: container,
        threshold: 0.01
      }
    )

    io.observe(el)
  }

  const tryFind = () => {
    const el = document.getElementById(messageId)

    if (!el) return false
    waitUntilVisible(el)
    return true
  }

  // 1) элемент уже есть?
  if (tryFind()) {
    stopTracking = cleanup
    return
  }

  // 2) ждём появления элемента
  mo = new MutationObserver(() => {
    if (finished) return
    if (tryFind()) {
      if (mo) mo.disconnect()
      mo = null
    }
  })

  mo.observe(container, { childList: true, subtree: true })

  stopTracking = cleanup
}

const unreadReactionMessageId = computed(() => chatDialog.value?.unreadReactionMessage?.messageId)

watch(unreadReactionMessageId, (messageId) => {
        if (messageId === props.message?.messageId) {
          startTrackingMessageVisibility(messageId)
        }
      },
      {
        immediate: !!unreadReactionMessageId.value
      })

onBeforeUnmount(() => {
  if (stopTracking) stopTracking()
})
</script>

<template>
  <div v-show="reactionId">
    <div
      :class="{ 'animate-unread-flicker': chatDialog?.unreadReactionMessage?.messageId === message.messageId}"
      class="rounded-full bg-gray-200/80 p-1.5 reaction-bubble">
      <Transition name="reaction-pop" mode="out-in">
        <img
          v-if="reactionIcon"
          :key="reactionId"
          class="h-3.5 w-3.5 reaction-icon"
          :src="reactionIcon"
          alt="">
      </Transition>
    </div>

    <!--<pre>{{ !!chatDialog?.unreadReactionMessage }}</pre>-->
  </div>
</template>


<style scoped>
  .reaction-pop-enter-active {
    animation: reaction-pop-in 260ms cubic-bezier(.2, .9, .2, 1);
    transform-origin: 50% 70%;
  }

  .reaction-pop-leave-active {
    animation: reaction-pop-out 140ms cubic-bezier(.4, 0, 1, 1);
  }

  .reaction-pop-enter-from,
  .reaction-pop-leave-to {
    opacity: 0;
  }

  @keyframes reaction-pop-in {
    0% {
      opacity: 0;
      transform: scale(0.6) rotate(-10deg);
      filter: blur(1px);
    }
    55% {
      opacity: 1;
      transform: scale(1.3) rotate(6deg);
      filter: blur(0);
    }
    80% {
      transform: scale(0.96) rotate(-2deg);
    }
    100% {
      transform: scale(1) rotate(0deg);
    }
  }

  @keyframes reaction-pop-out {
    0% {
      opacity: 1;
      transform: scale(1);
    }
    100% {
      opacity: 0;
      transform: scale(0.7);
    }
  }

  .reaction-bubble {
    position: relative;
  }

  .reaction-bubble::after {
    content: "";
    position: absolute;
    inset: -2px;
    border-radius: 999px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    opacity: 0;
    transform: scale(0.9);
    pointer-events: none;
  }

  .reaction-bubble:has(.reaction-pop-enter-active)::after {
    animation: reaction-ring 360ms ease-out;
  }

  @keyframes reaction-ring {
    0% {
      opacity: 0;
      transform: scale(0.9);
    }
    40% {
      opacity: 1;
      transform: scale(1.1);
    }
    100% {
      opacity: 0;
      transform: scale(2.5);
    }
  }

  .reaction-icon {
    will-change: transform, opacity;
  }

  @keyframes unreadFlicker {
    0%, 100% {
      box-shadow: 0 0 0 0 rgba(244, 63, 94, 0.15);
    }
    50% {
      box-shadow: 0 0 0 5px rgba(244, 63, 94, 0.55);
    }
  }

  .animate-unread-flicker {
    animation: unreadFlicker 2s ease-in-out infinite;
  }
</style>